import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { Box, Dialog, Typography } from '@material-ui/core';
import { Controller, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';

import { createCompanyWithOwnerRequest } from '@vyce/core/src/api/companies';
import { AppOnboarding, HowItWorks, OnboardingStepTitle } from '@vyce/core/src/components';
import { Greetings } from '@vyce/core/src/components/onboarding';
import { DeviceContext } from '@vyce/core/src/contexts';
import { ActiveOnboardingStep, OnboardingStep, RequestPositionDataData } from '@vyce/core/src/types';
import { MAIN_CONTAINER_ID } from '@vyce/core/src/constants';
import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';
import useChatRequests from '@vyce/core/src/hooks/useChatRequests';
import { AppTextField } from '@vyce/core/src/components/inputs/AppTextField';
import { capitalizeEach } from '@vyce/core/src/utils';

import { useActions, useTypedSelector } from '../../../hooks';
import { InvitesContext } from '../../../store/invites-context';
import { OnboardingLoader } from '../components/OnboardingLoader';
import { EMPLOYER_STEPS } from '../constants';
import { JobTitleStep } from './steps';

export interface EmployerOnboardingForm {
  role: string;
  company: any;
}

export const EmployerOnboardingTrack: React.FC = () => {
  const { isPreparingOnboarding } = useContext(InvitesContext);
  const methods = useForm<EmployerOnboardingForm>({
    defaultValues: {
      role: '',
      company: undefined,
    },
  });
  const [submitting, setSubmitting] = useState<boolean>(false);
  const { isMobile } = useContext(DeviceContext);
  const [howItWorksOpen, setHowItWorksOpen] = useState(true);
  const { watch, setValue } = methods;
  const { handleServerError } = useContext(NotificationContext);
  const { access_token } = useTypedSelector(state => state.helper);
  const { first_name, email, phone, last_name, uuid } = useTypedSelector(state => state.user);
  const company = watch('company');
  const childRef = useRef<any>();
  const { me, setShowTour } = useActions();
  const history = useHistory();
  const { createCompanyChat } = useChatRequests({
    uid: uuid,
    name: `${first_name} ${last_name}`,
    email,
    phone,
  });

  const {
    control,
    formState: { errors },
  } = methods;

  const steps: OnboardingStep[] = useMemo(
    () => [
      {
        name: 'Greetings',
        component: (
          <Greetings firstName={first_name} subtitle="Now, let’s quickly create you an awesome profile!" />
        ),
      },
      {
        name: 'JobTitleStep',
        component: <JobTitleStep />,
      },
      {
        name: 'CompanyStep',
        component: (
          <Box>
            <OnboardingStepTitle text="What's your company name?" />

            <Typography variant="body2" color="textSecondary">
              It will be displayed on your company profile and on your workers` profiles.
            </Typography>

            <Controller
              control={control}
              rules={{ required: 'Company is required' }}
              name="company"
              render={({ field }) => (
                <AppTextField
                  {...field}
                  error={!!errors.company?.message}
                  label="Company Name"
                  margin="normal"
                  fullWidth
                  helperText={(errors.company?.message as string) || ''}
                />
              )}
            />
          </Box>
        ),
      },
    ],
    [first_name]
  );
  const [activeStep, setActiveStep] = useState<ActiveOnboardingStep>({ step: steps[0], number: 0 });

  useEffect(() => {
    if (access_token) {
      me();
    }
  }, [access_token]);

  useEffect(() => {
    childRef?.current?.scrollDown();
  }, [company]);

  useEffect(() => {
    setValue('company', capitalizeEach(company));
  }, [company]);

  const createCompanyWithOwner = async (position: RequestPositionDataData) => {
    try {
      const res = await createCompanyWithOwnerRequest(position);
      // Gateway returns CompanyCreated; createCompanyChat expects client Company shape.
      const company = res.data as any;

      await createCompanyChat(company, {
        email,
        phone,
        uid: uuid,
        name: `${first_name} ${last_name}`,
      });

      await me();
      setShowTour(true);
      history.push('/dashboard');
    } catch (e) {
      handleServerError(e);
    }
  };

  const handleNext = async (data: EmployerOnboardingForm) => {
    if (activeStep.number === steps.length - 1) {
      setSubmitting(true);
      const position = preparePositionData(data);
      await createCompanyWithOwner(position);
      setSubmitting(false);
    } else {
      childRef?.current?.nextStep();
    }
  };

  const preparePositionData = (data: EmployerOnboardingForm): RequestPositionDataData => ({
    role: data.role,
    company: {
      name: data.company,
    },
  });

  const closeHowItWorks = () => setHowItWorksOpen(false);

  if (isPreparingOnboarding) {
    return <OnboardingLoader />;
  }

  return (
    <Box paddingTop={10}>
      <AppOnboarding
        activeStep={activeStep}
        currentMessage=""
        setActiveStep={setActiveStep}
        methods={methods}
        handleNext={handleNext}
        steps={steps}
        loading={submitting}
        ref={childRef}
      />

      <Dialog
        fullScreen={isMobile}
        container={document.getElementById(MAIN_CONTAINER_ID)}
        open={howItWorksOpen}
        onClose={closeHowItWorks}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <HowItWorks steps={EMPLOYER_STEPS} handleClose={closeHowItWorks} />
      </Dialog>
    </Box>
  );
};
